// src\components\common\EnhancedProductionCalendar.jsx
import React from 'react';

const EnhancedProductionCalendar = ({ 
    resources, 
    timeSlots, 
    batchAssignments, 
    onBatchClick, 
    readOnly = true,
    pendingOrders // Ajoutez cette prop pour recevoir les OFs
  }) => {

  // Fonction pour obtenir la couleur du cycle
  const getCycleColor = (cycleName) => {
    switch(cycleName) {
      case 'A42': return 'bg-blue-500';
      case 'A35': return 'bg-purple-500';
      case 'E25': return 'bg-green-500';
      case 'A29': return 'bg-red-500';
      case 'E28': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };
  
  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-500';
      case 'in-progress': return 'bg-blue-500';
      case 'scheduled': return 'bg-gray-500';
      case 'waiting': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  // Calculer les stats d'OFs pour une fournée
  const getBatchOrderStats = (batchNumber) => {
    if (!pendingOrders || !batchNumber) return { completed: 0, total: 0 };
    
    const batchOrders = pendingOrders.filter(order => order.assignedTo === batchNumber);
    const completedOrders = batchOrders.filter(order => order.status === 'completed');
    
    return {
      completed: completedOrders.length,
      total: batchOrders.length
    };
  };

  // Regrouper les créneaux par date
  const dateGroups = timeSlots.reduce((groups, slot) => {
    if (!groups[slot.date]) {
      groups[slot.date] = [];
    }
    groups[slot.date].push(slot);
    return groups;
  }, {});
  
  // Obtenir les dates uniques pour l'en-tête
  const uniqueDates = Object.keys(dateGroups);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36 border-r sticky left-0 z-10 bg-gray-50">
                Ressource
              </th>
              {uniqueDates.map((date, index) => (
                <th key={index} colSpan={dateGroups[date].length} className="px-6 py-2 text-center text-sm font-medium text-gray-900 uppercase tracking-wider border-r last:border-r-0 bg-blue-50">
                  {date}
                </th>
              ))}
            </tr>
            <tr>
              <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 border-r sticky left-0 z-10 bg-gray-50"></th>
              {timeSlots.map(slot => (
                <th key={slot.id} className="px-3 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r last:border-r-0">
                  {slot.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {resources.map(resource => (
              <tr key={resource.id} className={resource.status !== 'available' ? 'bg-gray-50' : ''}>
                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50 border-r sticky left-0 z-10">
                  <div className="flex items-center">
                    <span className={`w-3 h-3 rounded-full mr-2 ${resource.status === 'available' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    {resource.name}
                  </div>
                </td>
                {timeSlots.map(slot => {
                  const assignment = batchAssignments.find(
                    a => a.resourceId === resource.id && a.timeSlotId === slot.id
                  );
                  
                  // Vérifier si des données d'affectation d'OF sont disponibles
                  const hasOrderData = assignment && (
                    typeof assignment.usedVacuumPorts !== 'undefined' || 
                    typeof assignment.usedTc !== 'undefined'
                  );

                  return (
                    <td key={slot.id} className="px-1 py-1 whitespace-nowrap text-sm text-gray-700 border-r last:border-r-0">
                      {assignment ? (
                        <div 
                          className={`p-2 rounded ${getCycleColor(assignment.cycleName)} bg-opacity-70 border-l-4 ${getCycleColor(assignment.cycleName)} ${readOnly ? '' : 'cursor-pointer hover:shadow-md'}`}
                          onClick={() => onBatchClick && onBatchClick(assignment)}
                        >
                          <div className="flex justify-between items-center">
                            <div className="font-bold">{assignment.batchNumber}</div>
                            <div className="font-bold">{assignment.cycleName}</div>
                          </div>
                          <div className="flex justify-between items-center mt-1 text-xs font-bold">
                            {pendingOrders ? (
                                <div>
                                    {(() => {
                                    const stats = getBatchOrderStats(assignment.batchNumber);
                                    return `${stats.completed}/${stats.total}`;
                                    })()}
                                </div>
                            ) : (
                              <div>&nbsp;</div>
                            )}
                            {assignment.status === 'in-progress' && (
                              <div className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full">
                                En cours
                              </div>
                            )}
                            {assignment.status === 'completed' && (
                              <div className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
                                Terminé
                              </div>
                            )}
                          </div> 
                        </div>
                      ) : resource.status === 'available' ? (
                        <div className="h-full w-full p-2 rounded border border-dashed border-gray-300 text-center text-xs text-gray-400">
                          Disponible
                        </div>
                      ) : (
                        <div className="h-full w-full p-2 rounded border border-dashed border-red-200 text-center text-xs text-red-400">
                          Indisponible
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EnhancedProductionCalendar;